import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";

// Slide visuals — map each division to its hero image
const SLIDE_IMAGES = [
  "/brand/ikel-hinge-promo-full.jpg",
  "https://images.pexels.com/photos/7195559/pexels-photo-7195559.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1600",
  "https://images.pexels.com/photos/1427107/pexels-photo-1427107.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1600",
];

// Division routes (each slide's CTA deep-links to its page)
const SLIDE_LINKS = ["/ikel-mt", "/furniture", "/logistics"];

const AUTOPLAY_MS = 5000;
const FADE_MS = 500;

export default function HeroCarousel() {
  const { t } = useLang();
  const slides = t.hero.slides || [];
  const total = slides.length || 1;

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const go = useCallback((n) => setIndex(((n % total) + total) % total), [total]);
  const next = useCallback(() => go(index + 1), [go, index]);
  const prev = useCallback(() => go(index - 1), [go, index]);

  // Autoplay
  useEffect(() => {
    if (paused || total <= 1) return undefined;
    timerRef.current = setTimeout(() => {
      setIndex((i) => (i + 1) % total);
    }, AUTOPLAY_MS);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [index, paused, total]);

  // Keyboard nav when hero is in focus
  useEffect(() => {
    const onKey = (e) => {
      if (e.target && ["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prev, next]);

  return (
    <section
      className="relative overflow-hidden bg-[#0B1B29] text-white"
      data-testid="hero-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-roledescription="carousel"
    >
      {/* Layered slides (all mounted, fading) */}
      {slides.map((slide, i) => (
        <div
          key={`slide-${i}`}
          className="absolute inset-0 transition-opacity ease-out"
          style={{
            transitionDuration: `${FADE_MS}ms`,
            opacity: i === index ? 1 : 0,
            pointerEvents: i === index ? "auto" : "none",
          }}
          aria-hidden={i !== index}
          data-testid={`hero-slide-${i}`}
        >
          <img
            src={SLIDE_IMAGES[i] || SLIDE_IMAGES[0]}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-55"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>
      ))}

      {/* Foreground content — swaps with the active slide */}
      <div className="relative pgb-container py-28 md:py-40 min-h-[520px] md:min-h-[640px]">
        <div className="max-w-4xl" key={`content-${index}`}>
          <span
            className="eyebrow text-[#93C5FD] anim-fade-up inline-block"
            style={{ animationDelay: "0ms" }}
          >
            {slides[index]?.kicker}
          </span>
          <h1
            className="mt-5 text-[44px] sm:text-6xl md:text-7xl font-black leading-[0.95] tracking-tighter anim-fade-up"
            style={{ animationDelay: "80ms", fontFamily: "Chivo" }}
            data-testid="hero-title"
          >
            <span className="block text-white">{slides[index]?.title}</span>
            <span className="block text-[#93C5FD] mt-1">
              {slides[index]?.headline}
            </span>
          </h1>
          <p
            className="mt-8 text-base md:text-xl text-white/90 max-w-2xl leading-relaxed anim-fade-up"
            style={{ animationDelay: "180ms" }}
          >
            {slides[index]?.subtitle}
          </p>
          <div
            className="mt-10 flex flex-wrap gap-4 anim-fade-up"
            style={{ animationDelay: "260ms" }}
          >
            <Link
              to="/quote"
              className="btn-accent"
              data-testid="hero-cta-quote"
            >
              {t.hero.slideCta} <ArrowRight size={16} />
            </Link>
            <Link
              to={SLIDE_LINKS[index] || "#divisions"}
              className="btn-secondary bg-white/5 text-white border-white/40 hover:bg-white hover:text-[#0B1B29]"
              data-testid="hero-cta-divisions"
            >
              {t.hero.ctaSecondary} <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        {/* Dot indicators */}
        <div
          className="absolute bottom-24 md:bottom-28 left-0 right-0 pgb-container flex items-center gap-3"
          data-testid="hero-dots"
        >
          {slides.map((_, i) => (
            <button
              key={`dot-${i}`}
              type="button"
              onClick={() => go(i)}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`hero-dot-${i}`}
              className={`h-[3px] transition-all ${
                i === index ? "w-12 bg-[#F59E0B]" : "w-6 bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
          <span className="ml-2 text-[10px] font-mono tracking-[0.22em] uppercase text-white/55">
            {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </span>
        </div>

        {/* Arrows (desktop) */}
        <button
          type="button"
          onClick={prev}
          aria-label={t.hero.slideAriaPrev}
          data-testid="hero-prev"
          className="hidden md:flex absolute left-5 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center border border-white/25 bg-[#0B1B29]/40 hover:bg-white hover:text-[#0B1B29] text-white transition-colors"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          type="button"
          onClick={next}
          aria-label={t.hero.slideAriaNext}
          data-testid="hero-next"
          className="hidden md:flex absolute right-5 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center border border-white/25 bg-[#0B1B29]/40 hover:bg-white hover:text-[#0B1B29] text-white transition-colors"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Bottom city ticker (kept) */}
      <div className="relative border-t border-white/10 bg-[#0B1B29]">
        <div className="overflow-hidden">
          <div className="flex gap-16 py-5 whitespace-nowrap animate-ticker">
            {[0, 1].map((k) => (
              <div
                key={`ticker-row-${k}`}
                className="flex gap-16 items-center text-white/60 font-mono text-xs tracking-[0.25em] uppercase"
              >
                <span>Foshan</span><span>·</span>
                <span>Guangzhou</span><span>·</span>
                <span>Shenzhen</span><span>·</span>
                <span>Ningbo</span><span>·</span>
                <span>Shanghai</span><span>·</span>
                <span>Baku</span><span>·</span>
                <span>Hamburg</span><span>·</span>
                <span>Dubai</span><span>·</span>
                <span>Moscow</span><span>·</span>
                <span>Almaty</span><span>·</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
